import { matchPath } from 'react-router-dom';
import { RUTAS, type DefRuta } from '../rutas';
import { puedeVerMarcaje } from './marcaje-acceso';
import { modulosDelEnlacePromos } from './promos-acceso';

/**
 * MODELO DE NAVEGACIÓN — transcripción del bloque `PMM v61` del monolito
 * (@87.892.315 `CAT`/`MODS`/`SUBPGS`, @87.894.642 `window.PMM`).
 *
 * Vive aparte de `Marco.tsx` porque hay DOS consumidores, igual que en el v88:
 * el marco lo usa para pintar el menú, y DHALIA para responder «¿dónde veo X?»
 * — el monolito se lleva una copia literal de `MODS`/`CAT`/`SUBPGS` dentro del
 * bloque del asistente (`RMODS`/`RCAT`/`RSUB`, @90.577.191) justo para eso.
 * Aquí es una sola fuente, que es mejor: la copia del v88 puede desincronizarse.
 *
 * ── POR QUÉ EL MÓDULO NO SE PUEDE DEDUCIR DE LA RUTA ────────────────────────
 * El v88 guarda el módulo elegido en `PMM.cur` y lo cambia con `setMod()`. NO lo
 * deduce de la página, y no podría: los submódulos están COMPARTIDOS —`vend`
 * vive en cei, cobranzas, comercial, finanzas y gerencia; `cli` en cuatro;
 * `marcas` en cinco—. `PMM.mark(pg)` solo busca un módulo cuando el actual no
 * contiene el submódulo de la página:
 *
 *     if(!m||!m.subs||m.subs.indexOf(sub)<0){ …el primero que lo tenga… }
 */

/** `CAT` del v88, verbatim y en su orden — el orden ES el del submenú. */
export const CAT: Record<string, { t: string; pg?: string; kids?: string[] }> = {
  vend:      { t: 'Vendedores', kids: ['vendedor', 'vd-evolucion', 'vd-analisis', 'vd-comportamiento'] },
  marcas:    { t: 'Marcas', pg: 'marcas' },
  lanz:      { t: 'Lanzamientos', pg: 'lanzamientos' },
  cli:       { t: 'Clientes', kids: ['clientes', 'cli-activacion', 'cli-tendencia', 'cli-analisis', 'cli-maestra', 'cli-ficha'] },
  // El v88 abre aquí sus 9 vistas `afin`; en el porte son PESTAÑAS de una sola
  // ruta (`Financiero.tsx`), como declara `rutas.tsx`. Un elemento, no nueve.
  fin:       { t: 'Financiero', pg: 'financiero' },
  metas:     { t: 'Metas', pg: 'metas' },
  /* Un tablero por empresa desde el 2026-10-15 (db/246): «esa seccion será
   * dividida en power query DDH y power query DHV». El 2026-10-26 (db/264) se
   * sumó el de Difranca. Misma audiencia las tres. */
  pquery:    { t: 'Power Query', kids: ['pquery', 'pquery-dhv', 'pquery-dif'] },
  /* El Simulador de Promociones fue kid de esta entrada UN día (2026-08-14,
   * PR #4): esa misma tarde la usuaria pidió unificarlo DENTRO de la pestaña
   * «Nueva solicitud» (`componentes/SimuladorPromo.tsx`), así que la sección
   * vuelve a ser una sola página. El menú se construye desde ESTE objeto, no
   * desde `rutas.tsx` ni desde la base — si una página nueva no se registra
   * aquí, queda huérfana y `navegacion.test.ts` lo caza. */
  nego:      { t: 'Negociaciones Comerciales', pg: 'nego' },
  /* SECCIÓN NUEVA (2026-08-26, pedida por la usuaria): Negociaciones Comerciales
   * replicada para Facturación y Finanzas, con la columna del N° de la nota de
   * crédito. Cuelga SOLO de esos dos módulos (las dos líneas de `MODULOS` de
   * abajo son el ENLACE, no el permiso: este menú no se lee de la base, así que
   * sin ellas los 10 usuarios con la página en el token solo podrían entrar
   * escribiendo /negociaciones-nc a mano — es lo que le pasó a Catálogos durante
   * dos días).
   *
   * Es una entrada APARTE de `nego` y no un hermano suyo, a propósito: `nego`
   * vive en OCHO módulos, así que meter la réplica dentro de esa entrada la
   * habría ofrecido también en Comercial, Cobranzas, C.E.I., Gerencia,
   * Inteligencia de Mercado y Trade en cuanto un usuario tuviera las dos
   * páginas. Lo que la usuaria pidió es justo lo contrario: «la seccion de
   * negociaciones comerciales de los demas modulos no debe ser modificada».
   * Ver db/072_negociacion_nc.sql. */
  'nego-nc': { t: 'Negociaciones · Notas de Crédito', pg: 'nego-nc' },
  /* SECCIÓN NUEVA (2026-09-29, pedida por la usuaria): Negociaciones BBK —
   * Negociaciones Comerciales solo para la marca BBK, por SKU vigente y sin
   * presupuesto—. Entrada APARTE de `nego` por lo mismo que `nego-nc`. Cuelga
   * de `comercial` y de `fact` (Facturación carga la NC dentro); las dos líneas
   * de `MODULOS` son el ENLACE, no el permiso. Ver db/190_nego_bbk.sql. */
  'nego-bbk': { t: 'Negociaciones BBK', pg: 'nego-bbk' },
  /* SECCIÓN NUEVA (2026-08-18, pedida por la usuaria): el Tablero de KPI por
   * vendedor. Cuelga de cei y comercial; qué fichas salen y quién carga lo
   * decide el endpoint por ROL (db/037_kpi.sql, netlify/functions/kpi.mts).
   * El 2026-09-04 la audiencia gana a FACTURACIONDDH, que la tenía podada por
   * el paquete de db/044 y entró por pedido expreso (db/132): son 9 roles.
   * VISUALIZAR, no cargar — `ROLES_CARGA_KPI` no se tocó. */
  kpis:      { t: "KPI's", pg: 'kpis' },
  /* SECCIÓN NUEVA (2026-08-25, pedida por la usuaria): el Reporte Flash de
   * Ventas (CEI-FOR-005). Cuelga SOLO de `comercial`. La línea de `MODULOS` de
   * abajo es el ENLACE, no el permiso: este menú no se lee de la base, así que
   * sin ella los 23 usuarios con la página en el token solo podrían entrar
   * escribiendo /reporte-flash a mano — es lo que le pasó a Catálogos durante
   * dos días. Ver db/061_reporte_flash.sql. */
  flash:     { t: 'Reporte Flash de Ventas', pg: 'flash' },
  /* SECCIÓN NUEVA (2026-08-26, pedida por la usuaria): el Reporte ACT/INACT
   * Clientes. Cuelga de DOS módulos y las dos líneas de `MODULOS` de abajo son
   * el ENLACE, no el permiso: este menú no se lee de la base, así que sin ellas
   * los 27 usuarios con la página en el token solo podrían entrar escribiendo
   * /reporte-act-inact a mano — es lo que le pasó a Catálogos durante dos días.
   *
   * Y las dos hacen falta por motivos DISTINTOS: `comercial` es donde la
   * usuaria pidió la sección, y `cobranzas` es el único módulo del usuario
   * COBRANZAS, al que ella nombró en la audiencia. Sin la segunda, «visible a
   * cobranzas» no se cumpliría aunque la base le concediera la página.
   * Ver db/082_act_inact_clientes.sql. */
  'act-inact': { t: 'Reporte ACT/INACT Clientes', pg: 'act-inact' },
  /* SECCIÓN NUEVA (2026-08-25, pedida por la usuaria): el MODELO DIAMANTE, con
   * sus TRES comités. Cuelga SOLO de `comercial`. Las tres páginas llevan
   * ADEMÁS el candado de participación de abajo (`pasaCandadoDiamante`), que es
   * lo que hace que un asesor sin cadenas en ese comité no vea el enlace — el
   * permiso de la base es por página y no distingue a los 8 asesores que
   * participan de los 7 que no. Ver db/068_modelo_diamante.sql. */
  /* Se llamó «Modelo Diamante» las primeras horas del 2026-08-25; la usuaria lo
   * renombró a **Comités** esa misma tarde, y con él los tres kids perdieron el
   * prefijo «Comité » que ahora sería redundante bajo esa cabecera. El id del
   * submódulo (`diamante`) y los de las páginas NO cambian: son claves de RBAC
   * que viven en el token, en `auth.pagina` y en las dos tablas de exclusión —
   * renombrarlas costaría una migración y no compra nada. El MÉTODO sigue
   * llamándose Modelo Diamante dentro de la ficha (es como lo llama la maqueta:
   * el emparejamiento área↔área). */
  diamante:  { t: 'Comités', kids: ['diam-cck', 'diam-ftd', 'diam-ivm'] },
  /* SECCIÓN NUEVA (2026-08-28, pedida por la usuaria): Inventario Multimax —
   * la réplica de INVENTARIO_GAMA.xlsx (inventario GA.MA en tiendas Multimax)
   * con sus gráficas y la carga del semanal. Cuelga de DOS módulos —
   * `comercial`, donde ella pidió la sección, y `tmk`, el único módulo de
   * TRADEMARKETING en esta familia—. Las dos líneas de `MODULOS` de abajo son
   * el ENLACE, no el permiso: este menú no se lee de la base, así que sin
   * ellas los 9 usuarios con la página en el token solo podrían entrar
   * escribiendo /inventario-multimax a mano — es lo que le pasó a Catálogos
   * durante dos días. Dentro de ASESOR y REGIONAL el corte es por USUARIO
   * (solo ASESOR319 y CENTROLLANOS la llevan en el token, db/093 §4), así que
   * aquí no hace falta ningún candado extra: `puedeVer` ya dice que no.
   * Ver db/093_inventario_multimax.sql. */
  invmultimax: { t: 'Inventario Multimax', pg: 'inv-multimax' },
  /* SECCIÓN NUEVA (2026-08-31, pedida por la usuaria): la planificación
   * mensual del COORDINADOR regional —evoluciona el VNT-FOR-013—, dentro de
   * Comercial. Que esté en este menú no la abre a todo Comercial: db/100 deja
   * fuera a los asesores, a Facturación DDH y a Gerencia de Finanzas con
   * exclusiones nominales, y `puedeVer` ya lo respeta. */
  plancoord: { t: 'Planificación Coordinadores', pg: 'plan-coord' },
  /* SECCIÓN NUEVA (2026-09-02, pedida por la usuaria): la sección de listas de
   * precios de GA.MA, con el nombre disimulado a propósito — «Portafolio
   * GA.MA», y sus dos pestañas dentro de UNA página («Matriz Vigente» e
   * «Incorporaciones»), no dos entradas de menú. Nació colgada solo de
   * `comercial`, que es donde ella la pidió, con los 31 usuarios de ese módulo
   * —FACTURACIONDDH incluido y ratificado—. Desde el **2026-09-08 cuelga
   * TAMBIÉN de `fact`** (db/142, pedido suyo): con eso entran FACTURACIONDHV y
   * FACTURACIONDIF y la audiencia son 33. Publicar sigue siendo más estrecho
   * que ver y lo comprueba el endpoint por rol (GERENCIACEI, ANALISTACEI,
   * MASTER): ningún usuario de facturación puede sustituir la matriz.
   * Ver db/117_portafolio_gama.sql y db/142_portafolio_facturacion.sql. */
  portafolio: { t: 'Portafolio GA.MA', pg: 'portafolio' },
  /* SECCIÓN NUEVA (2026-09-03, pedida por la usuaria): «Gestión de Clientes»,
   * el porte del Dashboard de Activación de C.E.I. con sus cinco pestañas
   * dentro de UNA página (Vista Grupo DH, las tres empresas y la Vista 360°),
   * no cinco entradas de menú. Cuelga SOLO de `comercial`, que es donde ella
   * la pidió, y la línea de `MODULOS` de abajo es el ENLACE, no el permiso:
   * este menú no se lee de la base, así que sin ella los 12 usuarios con la
   * página en el token solo podrían entrar escribiendo /gestion-clientes a
   * mano — es lo que le pasó a Catálogos durante dos días.
   *
   * Que esté en este menú NO la abre a todo Comercial: db/128 deja fuera a los
   * asesores, a Contraloría, a Facturación DDH y a Gerencia de Finanzas con
   * exclusiones nominales de rol, y `puedeVer` ya lo respeta. Los asesores
   * quedan fuera porque el tablero los COMPARA entre sí —subtotales por
   * región, ranking de ticket y Vista 360° de los 16—, y servirlo recortado a
   * un solo código dejaría la sección sin su contenido.
   *
   * Es una entrada APARTE de `act-inact` y no un hermano suyo: las dos son de
   * C.E.I. y las dos hablan de activación, pero `act-inact` es el DETALLE por
   * cliente (1.925 filas) y esta es el TABLERO por asesor y región (56), con
   * cifras —% sin KA, ticket, Δ pts— que la otra no tiene. Y sus audiencias no
   * coinciden: `act-inact` la ven 27 usuarios, asesores incluidos.
   * Ver db/128_gestion_clientes.sql. */
  gestcli:   { t: 'Gestión de Clientes', pg: 'gest-clientes' },
  /* SECCIÓN NUEVA (2026-09-25, pedida por la usuaria): «Informe Gestión
   * Coordinadores», el VNT-FOR-008 —Índice de Captación de Nuevos Clientes—
   * que cada coordinador regional completa hasta el 2do día hábil. Nació el
   * 23/09 como pestaña de `gestcli` (db/183) y ella la pidió aparte: «sacalo de
   * gestion de clientes». Cuelga SOLO de `comercial`, y la línea de `MODULOS`
   * de abajo es el ENLACE, no el permiso: este menú no se lee de la base.
   *
   * Que esté en este menú NO la abre a todo Comercial: db/186 le pone las
   * mismas exclusiones nominales de rol que lleva `gest-clientes` (asesores,
   * Contraloría, Facturación DDH, Gerencia de Finanzas y la cuenta personal de
   * Kenya), y `puedeVer` ya lo respeta. Ver db/186_informe_coordinadores.sql. */
  infcoord:  { t: 'Informe Gestión Coordinadores', pg: 'inf-coord' },
  /* SECCIÓN NUEVA (2026-09-16, pedida por la usuaria): «Frecuencia de Compra»,
   * el porte del libro `GrupoDH_Ventas_2026.xlsx` que ella armaba a mano y
   * mandaba por correo a la fuerza de ventas. Sus SEIS pestañas son las seis
   * hojas de análisis del libro dentro de UNA página, no seis entradas de menú.
   * Nació colgada SOLO de `comercial`, que es donde la pidió, y desde el
   * 2026-09-17 cuelga TAMBIÉN de `tmk` (db/165: «agregar al modulo de
   * trademarketing la seccion de frecuencia de compra»). Las dos líneas de
   * `MODULOS` son el ENLACE, no el permiso: este menú no se lee de la base, así
   * que sin ellas los usuarios con la página en el token solo podrían entrar
   * escribiendo /frecuencia-compra a mano — le pasó a Catálogos dos días.
   *
   * Que esté aquí NO la abre a todo Comercial: db/160 deja fuera a Contraloría,
   * a Facturación DDH y a Gerencia de Finanzas con exclusiones nominales de rol,
   * y `puedeVer` ya lo respeta. Los ASESORES **sí** entran, al revés que en
   * `gestcli`: aquel tablero compara asesores entre sí y éste se RECORTA por
   * alcance, así que cada uno ve su propia cartera — que es justo lo que ella
   * pidió («los asesores solo vean sus clientes»).
   * Ver db/160_frecuencia_compra.sql. */
  frecuencia: { t: 'Frecuencia de Compra', pg: 'frecuencia' },
  /* SECCIÓN NUEVA (2026-10-01, pedida por la usuaria): «Promociones», el
   * REGISTRO de las promos del 4T-2026 que acepta cada cliente en la app, con
   * su aviso por correo y el botón de «concluido» de Facturación (db/198).
   * ABIERTA el 2026-10-02 (db/205): cuelga de `comercial`, `fact` y `tmk`, y el
   * enlace salía en UNO solo por usuario; desde el 2026-10-08 sale además en
   * «Facturación» para quien tenga ese módulo, los Master incluidos
   * (`modulosPromos`, `src/lib/promos-acceso.ts`). */
  promos: { t: 'Promociones', pg: 'promos' },
  cartera:   { t: 'Cartera de Clientes', kids: ['cart-clientes', 'cart-historial', 'cart-cxc', 'cart-diascalle', 'cart-cerradas', 'cart-pagos'] },
  /* SECCIÓN NUEVA (2026-08-17, pedida por la usuaria): el Planificador de
   * Rutas y Cobranzas, APARTE de Cartera — entrada propia del submenú de
   * Cobranzas. Ver db/030_planificacion.sql y ESPEC_MODULO_PLANIFICACION.md. */
  /* Desde el 2026-08-31 es un GRUPO: el planificador + el Seguimiento a la
   * Planificación (db/099), que es el porte de la maqueta de SEGUIMIENTO
   * PLANIFICACION/. */
  /* Desde el 2026-09-01 cuelga de DOS módulos —`cobranzas`, de quien es la
   * sección, y `almacen`, que es lo único que trae a ALMACENYDESPACHO y a los
   * tres usuarios de Facturación (db/107)—. Los cuatro entran en SOLO LECTURA:
   * el candado de escritura es `ROLES_EDITAN_PLAN`, no el módulo. */
  /* Desde el 2026-09-17 el grupo tiene TRES: el planificador, el Seguimiento
   * (db/099) y el Cumplimiento (db/166), que mide lo mismo que se carga allí —
   * un solo porcentaje, y sin subida propia. Sin esta línea los usuarios
   * tendrían la página en el token y NINGÚN enlace (el menú no se lee de la
   * base: le pasó a Catálogos durante dos días). */
  /* Desde el 2026-09-30, CUATRO: el Marcaje de visita (db/192), en período de
   * prueba —asesores, coordinadores, Master, Cobranzas y Gerencia de Ventas
   * desde db/197, `pasaCandadoMarcaje`—, y SOLO en el menú de Cobranzas, que es
   * de donde cuelga en la base: el grupo se repite en Almacén y el enlace
   * saldría dos veces. */
  planificacion: { t: 'Planificación', kids: ['planificacion', 'plan-seguimiento', 'plan-cumplimiento', 'marcaje'] },
  /* SECCIÓN NUEVA (2026-08-25, pedida por la usuaria): el porte de la ficha en
   * papel ADF-FOR-012 «FICHA DEL CLIENTE». Cuelga de DOS módulos —`cobranzas`,
   * que es de quien es la sección, y `almacen`, que es lo único que trae a
   * ALMACENYDESPACHO y a FACTURACION (los dos usuarios de Facturación, DHV y
   * DIF)—. Las dos líneas de `MODULOS` de abajo son el ENLACE, no el permiso:
   * este menú no se lee de la base, así que sin ellas los 35 usuarios con la
   * página en el token solo podrían entrar escribiendo /prospecto-cliente a
   * mano — es lo que le pasó a Catálogos durante dos días. Ver db/066. */
  prospecto: { t: 'Prospecto Nuevo Cliente', pg: 'prospecto' },
  trade:     { t: 'Trade', pg: 'trade' },
  /* Cuelga de TRES módulos —tmk, comercial y gerencia— desde el 2026-08-20:
   * la usuaria pidió que la vieran los asesores, los coordinadores, las
   * gerencias y los master. Por `tmk` ya entraban asesores, master y las tres
   * gerencias con módulo de Trade; `comercial` es lo que trae a los cuatro
   * COORDINADORES (rol REGIONAL) y `gerencia` al rol GERENCIA. Ver
   * db/046_gtm_audiencia.sql, que lo comprueba rol por rol y usuario por
   * usuario, y que además EXCLUYE a FACTURACIONDDH: tiene `comercial` desde
   * db/044 y la habría heredado de rebote.
   * Estas dos líneas de `MODULOS` son el ENLACE, no el permiso: el menú se
   * construye desde aquí y no de la base, así que sin ellas los coordinadores
   * tendrían la página en el token y solo podrían entrar escribiendo
   * /go-to-market a mano — es lo que le pasó a Catálogos durante dos días. */
  gtm:       { t: 'Go To Market', pg: 'gtm' },
  /* SECCIÓN NUEVA (2026-08-08, pedida por la usuaria): junta las páginas del
   * negocio con Farmatodo. `bitftd` deja de ser un submódulo suelto. El
   * 2026-08-19 entró la tercera: `nego-ftd`, Negociaciones Comerciales
   * replicada solo para Farmatodo (db/040). El 2026-09-03 entró la CUARTA:
   * `ftd-portal`, el Portal FTD (db/127) — el sell-out de Farmatodo en
   * unidades, réplica de `Farmatodo/FTD_Portal_2.html`.
   *
   * VA PRIMERA a propósito: es la vista general del negocio (resumen, marcas,
   * geografía y rankings) y las otras tres son registros de gestión — bitácora
   * de actividades, notas de crédito y negociaciones. Quien abre la sección
   * busca la cifra antes que el registro.
   *
   * Y es la única de las cuatro con audiencia PROPIA: `LISTA_PORTAL_FTD`, diez
   * personas, más estrecha que la de la sección (que hoy alcanza a 18). Desde
   * el 2026-10-08 son catorce: entran Trade Marketing e I+D (db/227).
   *
   * La QUINTA, desde el 2026-11-03: `expo-maracaibo`, el informe de la
   * Expobelleza Maracaibo 2026 (db/285). Va SEGUNDA, junto al Portal FTD: las
   * dos son cifra, las otras tres son registro. También con audiencia propia,
   * `LISTA_EXPO_MARACAIBO`, catorce personas. */
  farmatodo: { t: 'Farmatodo', kids: ['ftd-portal', 'expo-maracaibo', 'bitftd', 'ncft', 'nego-ftd'] },
  /* SECCIÓN NUEVA (2026-08-08, pedida por la usuaria): el material de venta por
   * marca. Cuelga de SEIS módulos —tmk, comercial, gerencia, cobranzas, almacen
   * y fact— porque el acceso es por MÓDULO y no hay uno solo que tengan todos
   * los roles: colgarla solo de tmk dejaba fuera a asesores y regionales, y
   * colgarla de esos tres dejaba fuera a Cobranzas, Almacén y Despacho y
   * Facturación. Con los seis la ven TODOS los usuarios, que es lo que pidió la
   * usuaria el 2026-08-17. Ver db/039_catalogos_todos.sql, que lo comprueba rol
   * por rol. Estas tres líneas de abajo son el ENLACE: el permiso ya estaba en
   * la base desde el 17, pero este menú no se lee de la base, así que hasta el
   * 2026-08-19 los cinco usuarios de esos tres módulos solo podían entrar
   * escribiendo /catalogos a mano.
   * EXCEPCIÓN (2026-08-20, pedida por la usuaria): a FACTURACIONDDH el enlace
   * le sale SOLO en Comercial — sus otros dos módulos con Catálogos (Almacén y
   * Cobranzas) se lo esconden. El corte vive en `paginasDe` y va por ROL; la
   * página no se le quita. Ver `catalogosSoloEnComercial`.
   *
   * Desde el 2026-08-27 tiene una segunda página, `catalogos-admin`
   * («Administrar»), que cuelga SOLO de `tmk`: es el único módulo que tienen a
   * la vez DISEÑO y MASTER, los dos únicos que administran. Por eso este
   * submódulo pasa de `pg` a `kids`, como el de Información de Interés — el
   * enlace solo aparece a quien tenga la página en el token, así que a los
   * demás el menú les sigue saliendo idéntico. Ver db/085. */
  /* SECCIÓN NUEVA (2026-09-22, pedida por la usuaria): NIVEL DE SERVICIO SLA —
   * la demanda de los consignatarios (lo que la cadena pidió en su orden de
   * compra) contra lo realmente despachado, del primer modelo de medición que
   * envió Peter por agosto 2026. Cuelga de DOS módulos —`comercial`, donde
   * nació, y `almacen` desde db/180, que es lo único que trae a la cuenta
   * ALMACENYDESPACHO—, y las dos líneas de `MODULOS` de abajo son el ENLACE, no
   * el permiso: este menú no se lee de la base, así que sin ellas las siete
   * cuentas con la página en el token solo podrían entrar escribiendo
   * /nivel-servicio-sla a mano — es lo que le pasó a Catálogos durante dos
   * días.
   *
   * Que esté en este menú NO la abre a todo Comercial ni a todo Almacén: la
   * audiencia se poda NOMINALMENTE rol por rol, y `puedeVer` ya lo respeta —
   * al que está podado el enlace no le sale. Hoy la ven TRES roles y siete
   * cuentas: ALMACENYDESPACHO, GERENCIAVENTAS y MASTER. Es el patrón de
   * `plan-coord`, y hace falta porque en `auth` no hay tabla `rol_pagina` —
   * lo único que concede es el módulo entero. Ver db/178 (la sección) y
   * db/180 (esta audiencia). */
  sla:       { t: 'Nivel de Servicio SLA', pg: 'sla' },
  /* SECCIÓN NUEVA (2026-09-23, pedida por la usuaria): TRAZABILIDAD, el
   * CUADRO_CONTROL_PRESPUESTO MARKETING de Trade Marketing. Cuelga SOLO de
   * `tmk`. La línea de `MODULOS` de abajo es el ENLACE, no el permiso: la
   * audiencia la podan db/181 y db/184, y sin esa línea tendrían la página en
   * el token y ningún sitio donde pulsarla — lo que le pasó a Catálogos
   * durante dos días. Desde db/184 la ven SEIS cuentas con nombre
   * (`LISTA_TRAZ_MKT`): los tres roles que la tienen —TRADEMARKETING,
   * GERENCIAFINANZAS e IYD— ya traían `tmk`, así que el enlace sale solo. */
  traz:      { t: 'Trazabilidad', pg: 'traz-mkt' },
  /* SECCIÓN NUEVA (2026-09-23, pedida por la usuaria): CONTROL DE PEDIDOS —
   * Facturación sube su libro DSP-FOR-005 y de ahí salen las guías de
   * despacho. Cuelga de DOS módulos: `fact`, que trae a los tres usuarios de
   * Facturación (los que cargan), y `almacen`, que es lo único que trae a
   * ALMACENYDESPACHO (el que imprime las guías). Las dos líneas de `MODULOS`
   * de abajo son el ENLACE, no el permiso: el menú no se lee de la base, y sin
   * ellas las nueve cuentas tendrían la página en el token y ningún sitio
   * donde pulsarla — lo que le pasó a Catálogos durante dos días. A los otros
   * cinco roles de `almacen` (asesores, coordinadores, Cobranzas, Control de
   * Calidad y Gerencia de Ventas) db/182 se la poda nominalmente, así que el
   * enlace ni les aparece. */
  pedidos:   { t: 'Control de Pedidos', pg: 'ctrl-pedidos' },
  catalogos: { t: 'Catálogos', kids: ['catalogos', 'catalogos-admin'] },
  /* SECCIÓN NUEVA (2026-08-18, pedida por la usuaria): la infografía de los 7
   * pasos de la visita, que además es la PORTADA del módulo Comercial — su
   * `landing` de abajo. Cuelga SOLO de comercial, que es lo que se pidió: la ven
   * ASESOR, REGIONAL y MASTER (23 usuarios). Ver db/036_pasos_comerciales.sql. */
  pasos:     { t: 'Tus 7 pasos comerciales', pg: 'pasos-comerciales' },
  viaticos:  { t: 'Viáticos y Gastos', pg: 'viaticos' },
  inactcli:  { t: 'Inactivación Clientes', pg: 'inactcli' },
  devol:     { t: 'Devoluciones', pg: 'devol' },
  /* CINCO páginas desde el 2026-08-21: `an-insights` se mudó al submódulo
   * `@emp` (Resumen General) a pedido de la usuaria. La entrada sigue
   * llamándose Analítica y sigue teniendo su propio id de RBAC por página. */
  analitica: { t: 'Analítica', kids: ['an-comparador', 'an-pareto', 'an-waterfall', 'an-heatmap', 'an-whatif'] },
  /* Los dos ejes que el v88 trata aparte de `CAT`. En `@emp` el monolito pinta
   * las cuatro EMPRESAS; el porte no puede, porque el selector de empresa vive
   * DENTRO de `Resumen.tsx` y las cuatro `RESPGS` son páginas de verdad. */
  /* `an-insights` va la ÚLTIMA a propósito: las cuatro primeras conservan el
   * orden de `RESPGS` del v88, y la que llega nueva no se cuela en medio. Al
   * pie del Resumen ejecutivo hay una tira con los 3 hallazgos más graves y su
   * botón «Ver todos», que es por donde se entra de verdad. */
  '@emp':    { t: 'Resumen General', kids: ['resumen', 'evolucion', 'forecast', 'descarga', 'an-insights'] },
  '@ctor':   { t: 'Constructor', pg: 'ctor' },
  // MÓDULO NUEVO — no viene del v88. Ver 04_DOCS/ESPEC_MODULO_INFO_INTERES.md
  info:      { t: 'Información de Interés', kids: ['info', 'info-admin'] },
  /* MÓDULO NUEVO (2026-08-21, pedido de la usuaria) — Contraloría. Hoy tiene una
   * sola página; se deja como submódulo con `pg` (no `kids`) porque es lo que
   * hace `catalogos`, y añadirle hermanos el día que crezca es cambiar `pg` por
   * `kids`. Ver 04_DOCS/ESPEC_MODULO_BITACORA.md. */
  contraloria: { t: 'Bitácora de usuarios', pg: 'bitacora' },
  /* MÓDULO NUEVO (2026-09-01, pedido de la usuaria) — Medios y Comunicación.
   * Hoy tiene UNA sección, Prospectos RRSS; se deja con `pg` y no con `kids`
   * porque es lo que hace `contraloria`, y darle hermanos el día que crezca es
   * cambiar `pg` por `kids`. Ver db/110_medios_rrss.sql. */
  rrss: { t: 'Prospectos RRSS', pg: 'rrss' },
};

export interface DefModulo {
  id: string;
  nombre: string;
  /** `subs` del v88, en su orden. `@emp` / `@ctor` para los dos casos aparte. */
  submodulos: string[];
  /** `land` del v88: por qué submódulo se entra al pulsar el botón del módulo. */
  landing?: string;
}

/**
 * `MODS` del v88, verbatim — con el `land` de cada uno, que el porte no tenía.
 * El 12.º, `info`, NO viene del monolito: lo siembra `db/002_seed_rbac.sql`
 * (orden 12, concedido a todos los roles) y sin esta fila sus dos páginas
 * quedaban montadas y sin ningún botón que llevara a ellas.
 */
export const MODULOS: DefModulo[] = [
  { id: 'resumen',   nombre: 'Resumen General',         submodulos: ['@emp'] },
  /* `planificacion` entra el 2026-09-01 (db/107): la usuaria pidió que
   * ALMACENYDESPACHO y los TRES usuarios de Facturación (DHV, DIF y DDH) vean
   * la sección de Planificación de Cobranzas —el planificador y su
   * Seguimiento—, «pero no pueden editar la planificación». `almacen` es el
   * único módulo que tienen los cuatro, el mismo camino que db/066 usó para el
   * Prospecto. Va entre `devol` y `prospecto` por orden alfabético.
   * Esta línea es el ENLACE, no el permiso: el menú no se lee de la base, así
   * que sin ella los cuatro tendrían las páginas en el token y solo podrían
   * entrar escribiendo /planificacion a mano — es lo que le pasó a Catálogos
   * durante dos días. Y el permiso tampoco basta para escribir: quien guarda
   * el plan lo dice `ROLES_EDITAN_PLAN` (src/lib/plan-permisos.ts).
   * A CONTROLCALIDAD, que también tiene `almacen`, db/107 le excluye las dos
   * páginas nominalmente para que su menú quede exactamente como estaba. */
  /* `nego` entra el 2026-09-08 (db/140) por el MISMO camino y por el mismo
   * motivo: ALMACENYDESPACHO recibe por correo las negociaciones cuya
   * aplicación mueve mercancía (db/056) y hasta hoy el botón «Abrir en el
   * portal» lo mandaba a un 403 — «debe poder abrir la negociación para ver qué
   * productos debe entregar». Va entre `devol` y `planificacion` por orden
   * alfabético.
   * Lo que este enlace NO da es escritura: quien escribe lo dice
   * `src/lib/nego-permisos.ts`, que leen el endpoint y la página. Y a los otros
   * siete roles de `almacen` no les cambia nada — ASESOR, COBRANZAS, MASTER y
   * REGIONAL ya la veían por sus propios módulos, FACTURACION y FACTURACIONDDH
   * la tienen EXCLUIDA (y la exclusión gana a cualquier módulo), y a
   * CONTROLCALIDAD db/140 se la excluye nominalmente, igual que hizo db/107. */
  /* `pedidos` entra el 2026-09-23 (db/182) y va entre `nego` y
   * `planificacion` por orden alfabético: es donde ALMACENYDESPACHO ve el
   * control de pedidos de Facturación e imprime las guías de despacho. */
  /* `portafolio` entra el 2026-11-03 (db/284) entre `planificacion` y
   * `prospecto` por orden alfabético: «la seccion de portafolio ga.ma debe
   * aparecer al usuario almacen y despacho». `almacen` es su único módulo de
   * trabajo. Aquí solo le sale a quien no la ve ya por Comercial, Facturación
   * o Trade Marketing (`MODULOS_CON_PORTAFOLIO`): a los asesores,
   * coordinadores, Facturación y Master no se les duplica. El `landing` NO
   * cambia. */
  { id: 'almacen',   nombre: 'Almacén y Despacho',      submodulos: ['catalogos', 'devol', 'nego', 'pedidos', 'planificacion', 'portafolio', 'prospecto', 'sla'], landing: 'devol' },
  { id: 'cei',       nombre: 'C.E.I.',                  submodulos: ['analitica', 'cli', 'farmatodo', 'fin', 'kpis', 'lanz', 'marcas', 'metas', 'nego', 'pquery', 'vend'], landing: 'vend' },
  { id: 'cobranzas', nombre: 'Cobranzas',               submodulos: ['act-inact', 'cartera', 'catalogos', 'cli', 'inactcli', 'nego', 'planificacion', 'prospecto', 'vend'], landing: 'cartera' },
  /* `pasos` va PRIMERO y es el `landing`: la usuaria pidió (2026-08-18) que al
   * pulsar Comercial salga inmediatamente la infografía de los 7 pasos. Antes
   * entraba por `vend`, que es el `land` que el v88 traía para este módulo. */
  /* `flash` entra el 2026-08-25 y va detrás de `catalogos` por orden
   * alfabético, como el resto de la lista tras `pasos` (que va primero porque
   * es el `landing`). Lo ven 8 roles: los 7 de db/061/073 y FACTURACIONDDH,
   * que estaba podado y entró el 2026-09-04 por pedido expreso (db/132). El
   * enlace no hubo que tocarlo —esta línea ya estaba y el submenú filtra por
   * `puedeVer`—, así que le apareció con solo borrar la exclusión.
   * GERENCIAFINANZAS sigue fuera (db/073): tiene `comercial` pero no la página,
   * así que a él este enlace no le sale. */
  /* `diamante` entra el 2026-08-25 y va detrás de `cli` por orden alfabético,
   * como el resto de la lista tras `pasos`. Cuelga SOLO de Comercial, que es
   * donde la usuaria pidió la sección. */
  /* `invmultimax` entra el 2026-08-28 y va detrás de `gtm` por orden
   * alfabético, como el resto de la lista tras `pasos` (que va primero porque
   * es el `landing`). De los usuarios de este módulo lo llevan en el token
   * ASESOR319, CENTROLLANOS, GERENCIAVENTAS, GERENCIACEI y MASTER (db/093). */
  /* `pquery` entra el 2026-11-05 (db/289) y va detrás de `portafolio` por orden
   * alfabético. Lo piden los COORDINADORES de ventas (rol REGIONAL): los de
   * zona reciben la base recortada a sus asesores (`pq-tablero-recorte.ts`) y
   * KEYACCOUNTS la del país. En Comercial también lo ven quienes ya lo tenían
   * por C.E.I., Gerencia o Trade Marketing; a los demás roles de Comercial se
   * les excluyó en la base. */
  { id: 'comercial', nombre: 'Comercial',               submodulos: ['pasos', 'act-inact', 'catalogos', 'cli', 'diamante', 'farmatodo', 'flash', 'frecuencia', 'gestcli', 'gtm', 'infcoord', 'invmultimax', 'kpis', 'lanz', 'marcas', 'metas', 'nego', 'nego-bbk', 'plancoord', 'portafolio', 'pquery', 'promos', 'sla', 'vend'], landing: 'pasos' },
  /* 🔴 FACTURACIÓN SE QUEDA SIN «Negociaciones Comerciales» (2026-08-26, db/079).
   * La usuaria lo pidió así de literal: «en el modulo de facturacion vamos a
   * eliminar negociaciones comerciales … y solo vamos a dejar Negociaciones ·
   * Notas de Credito», y ratificó que el recorte alcanza TAMBIÉN a los cuatro
   * administradores — que siguen viendo la sección comercial en sus otros siete
   * módulos, así que no pierden nada, solo deja de estar por duplicado aquí.
   * El submódulo `nego` sale de esta lista **y** de `modulo_pagina` en la base
   * (db/079 §2): esconder el enlace y dejar el permiso habría hecho que la
   * página siguiera abriéndose escribiendo /negociaciones a mano.
   *
   * Y por eso el `landing` pasa a ser la réplica: era `nego`, que ya no está.
   * Sin este cambio, `destinoDe` caía al primer submódulo visible y el botón
   * Facturación abría CATÁLOGOS — comprobado para FACTURACIONDHV, cuyo `fact`
   * ofrece Catálogos y la réplica.
   *
   * `portafolio` entra el 2026-09-08 (db/142): «los usuarios de facturacion
   * tienen acceso a todos los catalogo y al portafolio ga.ma». `fact` es el
   * portador porque lo tienen los TRES usuarios de facturación y, aparte de
   * ellos, solo MASTER: colgar la página aquí suma exactamente FACTURACIONDHV y
   * FACTURACIONDIF (31 → 33) sin darle `comercial` al rol FACTURACION. Va al
   * final por orden alfabético. FACTURACIONDDH la tenía desde db/117 por
   * Comercial y ahora le sale en los dos sitios: es la MISMA página, no un
   * permiso nuevo — el que decide sigue siendo el token. Esta línea es el
   * ENLACE, no el permiso: sin ella los dos entrarían escribiendo
   * /portafolio-gama a mano. */
  /* `pedidos` entra el 2026-09-23 (db/182) entre `nego-nc` y `portafolio`
   * por orden alfabético: el control de pedidos que Facturación carga y del
   * que salen las guías de despacho. El `landing` NO cambia. */
  /* `nego-bbk` entra el 2026-09-29 (db/190) detrás de `nego-nc`: Facturación
   * carga ahí la NC de las negociaciones BBK. El `landing` NO cambia. */
  /* `promos` entra el 2026-10-02 (db/205), al final: Facturación CONCLUYE ahí
   * los registros de promos de su empresa. El `landing` NO cambia. */
  { id: 'fact',      nombre: 'Facturación',             submodulos: ['catalogos', 'nego-nc', 'nego-bbk', 'pedidos', 'portafolio', 'promos'], landing: 'nego-nc' },
  /* `devol` entra el 2026-09-03 (db/130): «gerenciafinanzas debe tener acceso a
   * devoluciones pero no puede editar el seguimiento». `finanzas` es el módulo
   * más estrecho que tiene ese rol —9 usuarios activos, contra los 34 de `ctor`
   * o los 31 de `tmk`—, y de ellos el único que entraría de rebote es
   * GERENCIACEI, a quien db/130 excluye nominalmente (el mismo trato que db/107
   * le dio a CONTROLCALIDAD). Va entre `cli` y `farmatodo`, por orden alfabético.
   * Esta línea es el ENLACE, no el permiso: el menú no se lee de la base.
   * Y el permiso tampoco basta para escribir: quien registra un acta lo dice
   * `ROLES_REGISTRAN_DEVOL` (src/lib/devol-permisos.ts), y quien marca una
   * etapa, `ROLES_ACCION` en el endpoint — de las dos listas, Finanzas queda
   * fuera. */
  { id: 'finanzas',  nombre: 'Finanzas',                submodulos: ['analitica', 'cli', 'devol', 'farmatodo', 'fin', 'lanz', 'marcas', 'metas', 'nego', 'nego-nc', 'vend', 'viaticos'], landing: 'vend' },
  { id: 'gerencia',  nombre: 'Gerencia',                submodulos: ['analitica', 'catalogos', 'cli', 'farmatodo', 'fin', 'gtm', 'lanz', 'marcas', 'metas', 'nego', 'pquery', 'vend'], landing: 'vend' },
  { id: 'intel',     nombre: 'Inteligencia de Mercado', submodulos: ['analitica', 'fin', 'lanz', 'marcas', 'metas', 'nego'], landing: 'marcas' },
  /* `act-inact` entra el 2026-08-27 (db/085). Es el ENLACE, no el permiso: la
   * página se le concedió a las tres áreas nuevas —Medios y Comunicación,
   * Diseño e I+D— colgándola de `tmk`, que es el único módulo que tienen, y sin
   * esta línea la tendrían en el token sin ningún enlace que llevara a ella. A
   * AUDIOVISUAL y TRADEMARKETING no les sale: db/085 se la excluye para que su
   * menú quede exactamente como estaba. */
  /* `invmultimax` entra el 2026-08-28 (db/093): `tmk` es lo único que trae a
   * TRADEMARKETING. A los demás roles de este módulo que no fueron nombrados
   * (AUDIOVISUAL, DISEÑO, I+D, Medios…) db/093 §3 les excluye la página, así
   * que este enlace ni les aparece. */
  /* MEDIOS Y COMUNICACIÓN entra el 2026-09-01 (db/110) y va AQUÍ, entre
   * Inteligencia de Mercado y Trade Marketing, que es su sitio alfabético — el
   * mismo criterio que ordena la lista hasta Trade Marketing (Contraloría,
   * Constructor e Información de Interés van al final por decisión aparte).
   * Y es ESTE arreglo el que decide el orden de los botones:
   * `modulosVisibles()` recorre MODULOS en su orden y la columna `orden` de
   * `auth.modulo` no pinta ninguno.
   *
   * Trae UNA sola página, y eso es lo que hace que conceder el módulo sea
   * conceder exactamente esa página: no hace falta podar ningún rol. Colgarla
   * de `tmk` —el único módulo que hoy tiene MEDIOSYCOMUNICACION— se la habría
   * regalado de rebote a AUDIOVISUAL, DISEÑO e I+D. */
  { id: 'medios',    nombre: 'Medios y Comunicación',   submodulos: ['rrss'], landing: 'rrss' },
  /* `frecuencia` entra el 2026-09-17 (db/165): «agregar al modulo de
   * trademarketing la seccion de frecuencia de compra». Va entre `farmatodo` y
   * `gtm` por orden alfabético, como el resto de la lista. `tmk` es el portador
   * porque es lo único que trae a TRADEMARKETING —el mismo que usaron db/085 y
   * db/093—, y esta línea es el ENLACE, no el permiso: sin ella tendría la
   * página en el token y entraría escribiendo /frecuencia-compra a mano.
   * A los cuatro roles de área que `tmk` arrastra (AUDIOVISUAL, DISEÑO, I+D y
   * MEDIOSYCOMUNICACION) db/165 §2 les excluye la página, así que este enlace
   * ni les aparece; y CONTRALORIA y GERENCIAFINANZAS siguen fuera por db/160,
   * que los excluyó por ROL — un módulo nuevo no levanta esa exclusión. */
  /* `traz` entra el 2026-09-23 (db/181) y va al final, detrás de `trade`, por
   * orden alfabético. Es el ENLACE: el permiso lo da el token, y db/181 se lo
   * quita a los once roles de `tmk` que no son TRADEMARKETING ni MASTER, así
   * que a un asesor, a las áreas o a los analistas de Trade ni les aparece. */
  /* `promos` entra el 2026-10-02 (db/205) para la cuenta TRADEMARKETING, que
   * no tiene `comercial`. db/205 excluye a los roles de `tmk` que la usuaria
   * no nombró, y a quien tiene `comercial` el enlace le sale allí, no aquí. */
  /* `nego-bbk` y `portafolio` entran el 2026-10-15 (db/245): «a los tres usuarios de trade» (TRADEMARKETING y
   * los dos analistas, que no tienen `comercial`). db/245 los cuelga de `tmk` y poda a los roles de área que
   * `tmk` arrastraría; y a quien tiene `comercial` el enlace le sigue saliendo allí y NO aquí
   * (`SOLO_EN_TMK_SIN_COMERCIAL`), para no duplicarle la entrada a los asesores, gerencias y Master. */
  /* `cli` entra el 2026-10-15 (db/248) SOLO con la Ficha de clientes: «dale acceso a esa seccion a los
   * usuarios de trade». En Trade Marketing el grupo Clientes enseña únicamente `cli-ficha`
   * (`SOLO_FICHA_EN_TMK`) y solo a quien no tiene `comercial`, como `nego-bbk` y `portafolio`. */
  { id: 'tmk',       nombre: 'Trade Marketing',         submodulos: ['act-inact', 'catalogos', 'cli', 'farmatodo', 'frecuencia', 'gtm', 'invmultimax', 'lanz', 'marcas', 'nego', 'nego-bbk', 'portafolio', 'pquery', 'promos', 'trade', 'traz'], landing: 'marcas' },
  /* CONTRALORÍA va AQUÍ, entre Trade Marketing y Constructor, porque la usuaria
   * pidió esa posición exacta (2026-08-21) — y ES ESTE ARREGLO el que la decide:
   * `modulosVisibles()` recorre MODULOS en su orden, así que la columna `orden`
   * de auth.modulo (que db/052 renumera a la par) no pinta ningún botón. Lo ven
   * MASTER, CONTRALORIA y GERENCIAVENTAS por concesión del módulo en db/052 §6;
   * a los demás roles ni les aparece porque no lo tienen en el token. */
  { id: 'contraloria', nombre: 'Contraloría',           submodulos: ['contraloria'] },
  { id: 'ctor',      nombre: 'Constructor',             submodulos: ['@ctor'] },
  { id: 'info',      nombre: 'Información de Interés',  submodulos: ['info'] },
];

export const RUTA_DE: Record<string, DefRuta> =
  Object.fromEntries(RUTAS.map((r) => [r.pagina, r]));

/**
 * Qué elemento del submenú se marca para la página en curso. Solo tres páginas
 * no se marcan a sí mismas, y ninguna es un capricho:
 * · `cartera` es la portada del módulo y `Cartera.tsx` pinta el reporte de Base
 *   de clientes (el `navTo` envuelto del v88 hace `kid='cart-clientes'`);
 * · las dos rutas con parámetro marcan su padre.
 */
export const MARCA_COMO: Record<string, string> = {
  cartera: 'cart-clientes',
  'marca-detail': 'marcas',
  'lanz-detail': 'lanzamientos',
};

/** La ruta en curso. `matchPath` es lo que resuelve `/marcas/:marcaId`. */
export function rutaDePathname(pathname: string): DefRuta | null {
  const exacta = RUTAS.find((r) => r.ruta === pathname);
  if (exacta) return exacta;
  return RUTAS.find((r) => r.ruta.includes(':') && matchPath(r.ruta, pathname)) ?? null;
}

/**
 * Los permisos que necesita cualquier cálculo de navegación. Se pasan como
 * objeto para que el motor sea puro y se pueda probar sin React.
 */
/**
 * La copia de cliente de `LISTA_BLANCA_FARMATODO` (`_lib/auth.ts`). Ver el
 * comentario de `pasaCandadoFarmatodo`: son dos porque el módulo del servidor no
 * se puede importar aquí, y `navegacion.test.ts` comprueba que esta contenga a
 * los tres nombres para que un olvido salga en rojo y no en una pantalla vacía.
 */
export const LISTA_BLANCA_FARMATODO: readonly string[] = ['MASTER', 'KEYACCOUNTS', 'FACTURACIONDDH'];

/**
 * LA LISTA DEL PORTAL FTD (db/127) — la copia de cliente de `LISTA_PORTAL_FTD`
 * de `netlify/functions/_lib/auth.ts`, que es la que MANDA porque es la que da
 * o niega los datos; esta solo decide si se pinta el enlace.
 *
 * Va repetida a mano por la misma razón que `LISTA_BLANCA_FARMATODO`: ese
 * módulo arrastra `jose` y el driver de Neon, e importarlo mete el servidor
 * entero en el bundle del navegador. `navegacion.test.ts` comprueba que las dos
 * digan lo mismo, nombre por nombre.
 *
 * Los diez que la usuaria nombró el 2026-09-03, y los cuatro de Trade e I+D
 * que añadió el 2026-10-08 («TODOS LOS USUARIOS DE TRADE Y DE IYD PUEDEN VER
 * LA SECCION PORTAL FTD», db/227). Lo que esta lista hace y el
 * RBAC no puede: dejar fuera a **TI**, que es MASTER y por tanto recibe la
 * página en el token pase lo que pase (`cargarPermisos()` no lee exclusiones
 * para un MASTER). Ver la cabecera de la constante del servidor.
 */
export const LISTA_PORTAL_FTD: readonly string[] = [
  'MASTER', 'FRANCISCODIAZ', 'CARELADIAZ', 'DANIELAVERMIGLIO',   // 4 de los 5 MASTER — TI no
  'GERENCIACEI', 'ANALISTACEI',                                   // C.E.I.
  'GERENCIAFINANZAS', 'SUBGERENCIAFINANZAS',                      // las dos de Finanzas (db/233, 2026-10-12)
  'GERENCIAVENTAS', 'KENYA',                                      // Gerencia de Ventas y su asistente
  'KEYACCOUNTS',                                                  // el dueño del negocio FTD (vend. 103)
  'TRADEMARKETING', 'ANALISTATRADESENIOR', 'ANALISTATRADE',       // Trade Marketing (db/227)
  'IYD',                                                          // Investigación y Desarrollo (db/227)
  'ASISTENTEGERENCIA',                                            // Daniela Palma, consulta general (db/273)
];

/**
 * LOS MÓDULOS QUE CUELGAN EL PORTAL FTD EN SU MENÚ — Comercial (db/127 §2) y,
 * desde el 2026-09-08 a pedido de la usuaria, C.E.I. («agrega al modulo de cei
 * en farmatodo la seccion portal ftd»).
 *
 * Hace falta una lista porque el submódulo `farmatodo` lo comparten CINCO
 * módulos —cei, comercial, finanzas, gerencia y tmk— y `puedeVer` es por
 * usuario, no por módulo: sin este corte el enlace saldría en los cinco y
 * GERENCIACEI vería cinco enlaces a la misma página. Es el mismo corte de MENÚ
 * que `catalogosSoloEnComercial` (db/044), y por la misma razón: la base no
 * puede decirlo.
 *
 * ── POR QUÉ AÑADIR `cei` NO ABRE LA PÁGINA A NADIE, MEDIDO EL 2026-09-08 ────
 * Los usuarios con el módulo `cei` son SIETE —ANALISTACEI, GERENCIACEI y los
 * cinco MASTER— y los siete tienen TAMBIÉN `comercial`: los siete ya llevaban
 * `ftd-portal` en el token por esa vía. Esto pinta un SEGUNDO enlace a una
 * página que ya podían abrir, y por eso NO toca `auth.modulo_pagina`, que sigue
 * teniendo solo ('comercial', 'ftd-portal'): añadir ahí ('cei', 'ftd-portal')
 * se la regalaría a los usuarios de `cei` que nazcan después, y la audiencia de
 * esta página es CERRADA (db/127 §4 y §5, menor privilegio).
 *
 * Y `LISTA_PORTAL_FTD` sigue mandando por encima de esta lista: TI tiene `cei`
 * y NO ve el enlace ahí, igual que no lo ve en Comercial.
 *
 * ── `tmk` DESDE EL 2026-10-08 (db/227) ─────────────────────────────────────
 * Entraron TRADEMARKETING, los dos analistas de Trade e IYD, y ninguno tiene
 * `comercial` ni `cei`: su módulo es Trade Marketing, así que sin `tmk` aquí
 * tendrían la página en el token y ningún enlace. Al revés que con `cei`, esto
 * SÍ toca la base —('tmk', 'ftd-portal') en `auth.modulo_pagina`, con la poda
 * de AUDIOVISUAL, DISENO y MEDIOSYCOMUNICACION—. Efecto colateral, aceptado:
 * quien ya la veía y tiene también `tmk` (C.E.I., las gerencias, los MASTER)
 * ve el enlace además en la Farmatodo de Trade Marketing, donde ya veía las
 * otras tres páginas de la sección.
 */
export const MODULOS_PORTAL_FTD: readonly string[] = ['comercial', 'cei', 'tmk'];

/**
 * LA LISTA DE LA EXPOBELLEZA MARACAIBO (db/285) — la copia de cliente de
 * `LISTA_EXPO_MARACAIBO` de `netlify/functions/_lib/auth.ts`, que es la que
 * MANDA (da o niega el informe); esta solo decide si se pinta el enlace.
 * Repetida a mano por la misma razón que `LISTA_PORTAL_FTD`, y
 * `expo-maracaibo.test.ts` comprueba que digan lo mismo.
 *
 * Los trece que nombró la usuaria el 2026-11-03 y ASISTENTEGERENCIA, que sumó
 * ese mismo día por la regla de db/273. Lo que el RBAC no alcanza y esta lista
 * sí: dejar fuera a TI, que es MASTER.
 */
export const LISTA_EXPO_MARACAIBO: readonly string[] = [
  'MASTER', 'FRANCISCODIAZ', 'CARELADIAZ', 'DANIELAVERMIGLIO',   // 4 de los 5 MASTER — TI no
  'GERENCIACEI', 'ANALISTACEI',                                   // C.E.I.
  'GERENCIAFINANZAS',                                             // Finanzas — SUBGERENCIAFINANZAS no
  'GERENCIAVENTAS', 'KENYA',                                      // Gerencia de Ventas y su asistente
  'KEYACCOUNTS', 'ANALISTAKEYACCOUNTS',                           // Manuel Puerta y Haziel Jiménez
  'FACTURACIONDDH',                                               // Facturación DDH
  'TRADEMARKETING',                                               // Trade Marketing
  'ASISTENTEGERENCIA',                                            // Daniela Palma, consulta general (db/273)
];

/**
 * DÓNDE CUELGA EL ENLACE DE LA EXPOBELLEZA MARACAIBO — los mismos tres módulos
 * que el Portal FTD, y por lo mismo: la base la cuelga de `comercial` y `tmk`
 * (todos los de la lista tienen uno de los dos, medido el 2026-11-03), y `cei`
 * es la casa de C.E.I. Sin este corte el enlace saldría también en Finanzas y
 * Gerencia, que traen la sección Farmatodo.
 */
export const MODULOS_EXPO_MARACAIBO: readonly string[] = ['comercial', 'cei', 'tmk'];

/**
 * LA LISTA DE TRAZABILIDAD (db/181 + db/184) — la copia de cliente de
 * `LISTA_TRAZ_MKT` de `netlify/functions/_lib/auth.ts`, que es la que MANDA;
 * esta solo decide si se pinta el enlace. Repetida a mano por la misma razón
 * que `LISTA_PORTAL_FTD`, y `navegacion.test.ts` comprueba que digan lo mismo.
 *
 * Los seis que la usuaria nombró el 2026-09-23, ASISTENTEGERENCIA (db/273) y
 * SUBGERENCIAFINANZAS (db/281, 2026-10-30: «todo lo del módulo de
 * trademarketing», para las dos cuentas de Finanzas). Lo que el RBAC no
 * alcanza y esta lista sí: dejar fuera a DANIELAVERMIGLIO y a TI, que son
 * MASTER y llevan la página en el token pase lo que pase.
 */
export const LISTA_TRAZ_MKT: readonly string[] = [
  'MASTER', 'FRANCISCODIAZ', 'CARELADIAZ',   // 3 de los 5 MASTER — DANIELAVERMIGLIO y TI no
  'TRADEMARKETING',
  'GERENCIAFINANZAS', 'SUBGERENCIAFINANZAS', // las dos de Finanzas (Luisa desde db/281): ven, no cargan
  'IYD',                                     // Investigación y Desarrollo
  'ASISTENTEGERENCIA',                       // Daniela Palma, consulta general (db/273): ve, no carga
];

/**
 * LOS RESPONSABLES DE CADA COMITÉ DEL MODELO DIAMANTE, por código de vendedor.
 *
 * Es la copia de cliente de `core.diamante_cadena_vendedor` (db/068 §2). Va
 * repetida a mano por la misma razón que `LISTA_BLANCA_FARMATODO`: el menú se
 * pinta ANTES de cualquier petición y no puede consultar la base, y el módulo
 * del servidor arrastra `jose` y el driver de Neon al bundle del navegador.
 *
 * La de la BASE es la que manda —es la que da o niega los datos
 * (`diamante.mts` → `participa`)—; esta solo decide si se pinta el enlace.
 * `navegacion.test.ts` comprueba que las tres listas sigan siendo las mismas
 * que siembra la migración, para que un reparto nuevo salga en rojo y no en un
 * enlace que da 403.
 *
 *   CCK · las 26 cadenas de Cuentas Clave, repartidas entre 11 códigos
 *   FTD · Farmatodo, solo el 103 (Manuel Puerta)
 *   IVM · Ivoo y Multimax, solo el 319 (Carlos Méndez)
 *
 * El '613' (Edgar Primera) entró el 2026-09-10 con «Super 900», la cadena de
 * los cuatro códigos de Falcón que añadió db/147. **Los códigos no cambiaron
 * el 2026-09-14**, cuando db/150 partió esa cadena en las cuatro tarjetas de
 * sus cuatro clientes: las cuatro son suyas, así que la lista es la misma y lo
 * único que se movió es el conteo de arriba.
 */
export const RESPONSABLES_DIAMANTE: Record<string, readonly string[]> = {
  'diam-cck': ['102', '103', '209', '319', '411', '518', '612', '613', '714', '815', '816'],
  'diam-ftd': ['103'],
  'diam-ivm': ['319'],
};

export interface Permisos {
  tieneModulo: (id: string) => boolean;
  puedeVer: (pagina: string) => boolean;
  /**
   * El candado de la sección Farmatodo, idéntico al `candadoFarmatodo` de
   * `ncft-detalle.mts` y al `candadoD16` de `bitacora-ftd.mts`.
   *
   * En COMERCIAL la sección la ven solo los usuarios de la LISTA BLANCA (regla
   * de la usuaria, 2026-08-08) — es la D16 del v88 (gotcha 49: el monolito
   * esconde `bitftd` del módulo Comercial salvo MASTER/KEYACCOUNTS) aplicada
   * ahora a las TRES páginas. Fuera de Comercial la ve quien tenga el módulo,
   * que hoy son MASTER, GERENCIA, TRADEMARKETING y AUDIOVISUAL.
   *
   * La lista blanca es `LISTA_BLANCA_FARMATODO` de `netlify/functions/_lib/
   * auth.ts`, y aquí va REPETIDA a mano porque ese módulo arrastra `jose` y el
   * driver de Neon: importarlo mete el servidor entero en el bundle del
   * navegador. Las dos tienen que decir lo mismo — la de allí es la que manda,
   * porque es la que da o niega los datos; esta solo decide si se pinta el
   * enlace. `FACTURACIONDDH` entró el 2026-08-20 (db/044): su audiencia nueva
   * es Comercial recortado a Catálogos, Farmatodo, Lanzamientos y
   * Negociaciones, y sin esta línea tendría las tres páginas en el token y
   * ningún enlace que llevara a ellas.
   *
   * ⚠️ 2026-08-15: tener el módulo `tmk` ERA el proxy de «es Trade Marketing»,
   * y dejó de serlo — el rol ASESOR lo recibió para ver Marcas, Lanzamientos,
   * Catálogos y Go To Market, y NADA MÁS (db/028_asesor_alcance.sql). De ahí el
   * corte por rol: sin él, los 15 asesores verían la sección Farmatodo en el
   * menú de Trade Marketing. Es el mismo corte que hacen `candadoD16`
   * (bitacora-ftd.mts) y `candadoFarmatodo` (ncft-detalle.mts) — los tres
   * tienen que decir lo mismo o el menú ofrece enlaces que dan 403.
   */
  pasaCandadoFarmatodo: boolean;
  /**
   * El candado del PORTAL FTD (db/127), pareja de `candadoPortalFtd` en
   * `netlify/functions/ftd-portal.mts`. Los dos tienen que decir lo mismo o el
   * menú ofrece un enlace que da 403.
   *
   * Es más estrecho que `pasaCandadoFarmatodo`: la sección la ven 18 personas y
   * esta página DIEZ. Y no se puede expresar en la base —ni con
   * `rol_pagina_excluida` ni con `usuario_pagina_excluida`— porque el único que
   * hay que dejar fuera y no se puede es TI: es MASTER, y `cargarPermisos()`
   * cortocircuita a los MASTER con `SELECT id FROM auth.pagina` sin pasar por
   * ninguna exclusión. Ver `LISTA_PORTAL_FTD`.
   */
  pasaCandadoPortalFtd: boolean;
  /**
   * El candado de la EXPOBELLEZA MARACAIBO (db/285), pareja del de
   * `netlify/functions/expo-maracaibo.mts`. Por nombre de usuario, como el del
   * Portal FTD y por la misma razón: deja fuera a TI. Ver `LISTA_EXPO_MARACAIBO`.
   */
  pasaCandadoExpoMaracaibo: boolean;
  /**
   * El candado de TRAZABILIDAD (db/184), pareja de `candado` en
   * `netlify/functions/traz-mkt.mts`. Por nombre de usuario, como el del Portal
   * FTD y por la misma razón: deja fuera a dos MASTER. Ver `LISTA_TRAZ_MKT`.
   */
  pasaCandadoTraz: boolean;
  /**
   * El candado de MARCAJE DE VISITA (db/192 → db/197), pareja de `candado` en
   * `netlify/functions/marcaje.mts`. Aquí no hay copia de la regla: las dos
   * puertas importan la misma, `src/lib/marcaje-acceso.ts` (por ROL: asesores,
   * coordinadores y Master, más Cobranzas y Gerencia de Ventas).
   */
  pasaCandadoMarcaje: boolean;
  /**
   * PROMOCIONES (db/198 → db/205): los módulos donde sale su enlace, vacío si
   * no la ve. Pareja de `candado` en `netlify/functions/promos.mts`: las dos
   * puertas importan la misma regla, `src/lib/promos-acceso.ts`. La página
   * cuelga de tres módulos: Comercial (o Trade Marketing) y, desde el
   * 2026-10-08, también Facturación para quien la tenga; Facturación, solo ahí.
   */
  modulosPromos: readonly string[];
  /**
   * El recorte de Catálogos (2026-08-20, pedido de la usuaria): FACTURACIONDDH
   * tiene la sección en TRES de sus módulos —almacen, cobranzas y comercial,
   * los tres la cuelgan en `MODULOS`— y pidió verla solo en Comercial. No se
   * puede decir en la base: el permiso de página es por usuario, no por módulo
   * (`cargarPermisos` junta las páginas de todos sus módulos), y excluir
   * `catalogos` con `rol_pagina_excluida` se la quitaría también de Comercial.
   * Así que es un corte de MENÚ, como el candado de Farmatodo de arriba — pero
   * SIN pareja en el endpoint a propósito: su acceso a la página no cambia,
   * solo de qué módulos cuelga el enlace. Por ROL, no por username (NORMA 1:
   * la forma del dato no distingue perfiles), y el rol es de una sola persona
   * (db/044), así que el recorte no alcanza a nadie más.
   */
  catalogosSoloEnComercial: boolean;
  /**
   * VIÁTICOS para la fuerza de ventas (db/231, 2026-10-09): asesores y coordinadores reciben el módulo
   * `finanzas` SOLO para «Viáticos y Gastos». Las demás páginas de Finanzas que tienen en el token
   * (Vendedores, Clientes, Marcas, Negociaciones…) ya les salen en Comercial: aquí no se repiten, y el
   * botón Finanzas entra directo a Viáticos. Es un corte de MENÚ, sin pareja en ningún endpoint: el
   * acceso a cada página no cambia, solo de qué módulo cuelga el enlace.
   */
  finanzasSoloViaticos: boolean;
  /**
   * COMERCIAL RECORTADO para los roles de solo lectura (db/235, 2026-10-12):
   * Coordinación de Compras y Coordinación de Planificación y Costo ven de
   * Comercial «solo los 7 pasos, catálogos, frecuencia de compra, go to market,
   * lanzamientos, marca, portafolio gama, promociones» (la usuaria). Las demás
   * secciones de Comercial que tienen en el token les llegan por C.E.I. o por
   * Almacén y Despacho, que pidió ENTEROS (Clientes, KPI's, Metas, Vendedores,
   * SLA, Farmatodo…): no se pueden excluir en la base sin quitárselas también
   * allí, así que aquí se recorta el ENLACE. Mismo tipo de corte que
   * `finanzasSoloViaticos`, sin pareja en ningún endpoint: el acceso a cada
   * página no cambia. `null` = sin recorte.
   */
  comercialSolo: readonly string[] | null;
  /**
   * El candado de PARTICIPACIÓN del Modelo Diamante, pareja de `participa()` en
   * `netlify/functions/diamante.mts`. Los dos tienen que decir lo mismo o el
   * menú ofrece enlaces que dan 403.
   *
   * Un usuario con alcance de vendedor ve el comité solo si alguno de sus
   * códigos es responsable de alguna de sus cadenas; quien no tiene tope de
   * vendedor pasa, porque para él ya decidió el RBAC (db/068 §4 dejó dentro a
   * GERENCIACEI, CONTRALORIA y MASTER, y fuera a los otros cuatro roles con
   * `comercial`).
   *
   * Por qué hace falta ADEMÁS de la exclusión por usuario que graba db/068 §5:
   * la de la base es la que quita la página del token, y esta la que sigue
   * siendo cierta mientras ese token vive sus 8 horas si la usuaria mueve una
   * cadena de asesor entretanto. Son la misma regla en los dos sitios donde se
   * puede aplicar, y ninguna sobra.
   */
  participaEnComite: (pagina: string) => boolean;
}

/** Los submódulos de Comercial que ven los roles de solo lectura (db/235), en
 *  las palabras de la usuaria: «comercial solo los 7 pasos, catálogos,
 *  frecuencia de compra, go to market, lanzamientos, marca, portafolio gama,
 *  promociones». Ver `comercialSolo`. */
export const COMERCIAL_SOLO_LECTURA: readonly string[] = [
  'pasos', 'catalogos', 'frecuencia', 'gtm', 'lanz', 'marcas', 'portafolio', 'promos',
];

export function permisosDeSesion(s: {
  roles: string[]; modulos: string[]; paginas: string[]; usuario: { username: string };
  alcance?: { vendedores: string[] | null };
} | null): Permisos {
  return {
    tieneModulo: (id) => !!s?.modulos.includes(id),
    puedeVer: (pg) => !!s?.paginas.includes(pg),
    participaEnComite: (pagina) => {
      const resp = RESPONSABLES_DIAMANTE[pagina];
      if (!resp) return true;                       // no es una página del Diamante
      const mios = s?.alcance?.vendedores;
      if (!mios) return true;                       // sin tope de vendedor: decide el RBAC
      return mios.some((v) => resp.includes(v));
    },
    pasaCandadoFarmatodo: !!s && (
      (['tmk', 'cei', 'gerencia', 'finanzas'].some((m) => s.modulos.includes(m))
        /* REGIONAL desde db/231: los coordinadores reciben `finanzas` solo por Viáticos, y antes no
         * pasaban este candado. Mismo corte en `ncft-detalle.mts` y `negociaciones-ftd.mts`. */
        && !s.roles.includes('ASESOR') && !s.roles.includes('REGIONAL'))
      || LISTA_BLANCA_FARMATODO.includes(s.usuario.username)
    ),
    pasaCandadoPortalFtd: !!s && LISTA_PORTAL_FTD.includes(s.usuario.username),
    pasaCandadoExpoMaracaibo: !!s && LISTA_EXPO_MARACAIBO.includes(s.usuario.username),
    pasaCandadoTraz: !!s && LISTA_TRAZ_MKT.includes(s.usuario.username),
    pasaCandadoMarcaje: !!s && puedeVerMarcaje(s.usuario.username, s.roles),
    modulosPromos: s ? modulosDelEnlacePromos(s.roles, s.modulos) : [],
    catalogosSoloEnComercial: !!s?.roles.includes('FACTURACIONDDH'),
    finanzasSoloViaticos: !!s && (s.roles.includes('ASESOR') || s.roles.includes('REGIONAL')) && !s.roles.includes('MASTER'),
    /* Por ROL, no por `esSoloLectura`: REPORTESEXTERNO (db/242) también es de
     * solo lectura y sus catorce páginas ya vienen recortadas en la base; con
     * este corte perdería Flash, KPI's, Negociaciones, Comités y Vendedores. */
    comercialSolo: s && !s.roles.includes('MASTER') && s.roles.includes('COORDCOMPRASCOSTO')
      ? COMERCIAL_SOLO_LECTURA : null,
  };
}

/**
 * Las páginas que cuelgan de `tmk` para Trade Marketing (db/245, 2026-10-15) y que el resto ya ve en Comercial. En
 * Trade Marketing solo le salen a quien no tiene `comercial` (TRADEMARKETING y ANALISTATRADEMKT): a los asesores,
 * coordinadores, gerencias, C.E.I. y Master el enlace les sigue saliendo donde estaba. Es solo el MENÚ: el permiso
 * no cambia (lo da el token).
 */
export const SOLO_EN_TMK_SIN_COMERCIAL: readonly string[] = ['nego-bbk', 'portafolio', 'cli-ficha'];

/**
 * Los módulos que ya ofrecen Portafolio GA.MA (db/117, db/142, db/245). En Almacén y Despacho (db/284, 2026-11-03)
 * la página solo le sale a quien no tiene ninguno de ellos —hoy ALMACENYDESPACHO, cuyo rol solo trae `almacen` e
 * `info`—: a FACTURACIONDDH, que la ve en Comercial y en Facturación, y a los demás roles de Almacén no se les
 * duplica el enlace. Es solo el MENÚ: el permiso no cambia (lo da el token).
 */
export const MODULOS_CON_PORTAFOLIO: readonly string[] = ['comercial', 'fact', 'tmk'];

/** Del grupo Clientes, en Trade Marketing solo cuelga la Ficha de clientes (db/248). */
const SOLO_FICHA_EN_TMK = 'cli-ficha';

export function modulosVisibles(p: Permisos): DefModulo[] {
  return MODULOS.filter((m) => p.tieneModulo(m.id));
}

/**
 * Las páginas visibles de un submódulo DENTRO de un módulo, en orden de `CAT`.
 * El módulo entra en la cuenta por el candado de la sección Farmatodo: en
 * Comercial se esconde ENTERA salvo KEYACCOUNTS (y MASTER), que es exactamente
 * lo que responden los dos endpoints. Ofrecer un enlace que va a dar 403 es
 * peor que no ofrecerlo.
 */
export function paginasDe(sub: string, moduloId: string, p: Permisos): string[] {
  const c = CAT[sub];
  if (!c) return [];
  /* El candado de la SECCIÓN en Comercial. Desde db/127 tiene una segunda
   * puerta: quien pasa el candado del Portal FTD entra aunque no pase el de la
   * sección — es el caso de KENYA, cuyo rol solo tiene `comercial` (ninguno de
   * tmk/cei/gerencia/finanzas) y que por tanto no pasaría el de arriba. No
   * abre nada de más: cada página sigue filtrándose una por una ahí abajo, y
   * las otras tres no están en su token. */
  if (sub === 'farmatodo' && moduloId === 'comercial'
      && !p.pasaCandadoFarmatodo && !p.pasaCandadoPortalFtd && !p.pasaCandadoExpoMaracaibo) return [];
  /* La tercera puerta, la de la Expobelleza Maracaibo (db/285), es más estrecha
   * que las otras dos: abre la sección SOLO para esa página. La necesita
   * ANALISTAKEYACCOUNTS, cuyo rol es ASESOR —no pasa el candado de la sección
   * ni está en la lista del Portal FTD— y que puede traer otras páginas de la
   * sección en el token que sus endpoints le negarían. */
  const soloExpoMaracaibo = sub === 'farmatodo' && moduloId === 'comercial'
    && !p.pasaCandadoFarmatodo && !p.pasaCandadoPortalFtd;
  if (sub === 'catalogos' && moduloId !== 'comercial' && p.catalogosSoloEnComercial) return [];
  if (moduloId === 'finanzas' && p.finanzasSoloViaticos && sub !== 'viaticos') return [];
  if (moduloId === 'comercial' && p.comercialSolo && !p.comercialSolo.includes(sub)) return [];
  const ids = c.kids ?? (c.pg ? [c.pg] : []);
  return ids.filter((pg) => {
    const r = RUTA_DE[pg];
    if (!r || r.oculta) return false;
    // El candado del Modelo Diamante recorta COMITÉ a COMITÉ, no la sección
    // entera: el 319 ve Cuentas Clave e IVOO y Multimax, pero no Farmatodo.
    if (!p.participaEnComite(pg)) return false;
    /* El Portal FTD tiene audiencia PROPIA y más estrecha que la sección
     * (db/127): sin esta línea, los 8 que pasan el candado de la sección y no
     * están en `LISTA_PORTAL_FTD` —TI incluido, que es MASTER y la lleva en el
     * token— verían un enlace que da 403. */
    if (pg === 'ftd-portal' && !p.pasaCandadoPortalFtd) return false;
    /* Y solo en los módulos de `MODULOS_PORTAL_FTD`: Comercial, donde la
     * usuaria la pidió el 2026-09-03 y de donde cuelga en la base, C.E.I., que
     * añadió el 2026-09-08, y Trade Marketing desde el 2026-10-08 (db/227). Sin
     * este corte el enlace saldría también en Finanzas y Gerencia —los otros
     * dos módulos que traen la sección Farmatodo—, porque el submódulo es
     * COMPARTIDO y `puedeVer` es por usuario, no por módulo. Ver la cabecera de
     * la constante. */
    if (pg === 'ftd-portal' && !MODULOS_PORTAL_FTD.includes(moduloId)) return false;
    /* La Expobelleza Maracaibo: audiencia por NOMBRE (TI fuera) y solo en
     * Comercial, C.E.I. y Trade Marketing (db/285). */
    if (soloExpoMaracaibo && pg !== 'expo-maracaibo') return false;
    if (pg === 'expo-maracaibo'
      && (!p.pasaCandadoExpoMaracaibo || !MODULOS_EXPO_MARACAIBO.includes(moduloId))) return false;
    /* Trazabilidad tiene audiencia por NOMBRE (db/184): sin esta línea,
     * DANIELAVERMIGLIO y TI —MASTER, con la página en el token— verían un
     * enlace que da 403. */
    if (pg === 'traz-mkt' && !p.pasaCandadoTraz) return false;
    /* Marcaje de visita (db/192 → db/197): por ROL y solo en Cobranzas. */
    if (pg === 'marcaje' && (!p.pasaCandadoMarcaje || moduloId !== 'cobranzas')) return false;
    /* Promociones (db/205): por ROL, y solo en los módulos que le tocan (Facturación incluido desde el 2026-10-08). */
    if (pg === 'promos' && !p.modulosPromos.includes(moduloId)) return false;
    /* Negociaciones BBK y Portafolio GA.MA en Trade Marketing (db/245): solo a quien NO tiene `comercial`. */
    if (moduloId === 'tmk' && SOLO_EN_TMK_SIN_COMERCIAL.includes(pg) && p.tieneModulo('comercial')) return false;
    /* Portafolio GA.MA en Almacén y Despacho (db/284): solo a quien no la ve ya por Comercial, Facturación o Trade. */
    if (moduloId === 'almacen' && pg === 'portafolio' && MODULOS_CON_PORTAFOLIO.some((m) => p.tieneModulo(m))) return false;
    /* Ficha de clientes en Trade Marketing (db/248): del grupo Clientes, solo ella. */
    if (moduloId === 'tmk' && sub === 'cli' && pg !== SOLO_FICHA_EN_TMK) return false;
    return p.puedeVer(pg);
  });
}

/** Los submódulos de un módulo que tienen al menos una página visible. */
export function submodulosDe(m: DefModulo, p: Permisos): string[] {
  return m.submodulos.filter((sub) => paginasDe(sub, m.id, p).length > 0);
}

/**
 * A dónde lleva el botón del módulo. Replica `PMM.go()`: `subGo(m.land ||
 * m.subs[0])`, y `subGo` abre `c.pg` o el PRIMER kid. Sin el `land`, Cobranzas
 * entraba por Vendedores en vez de por Cartera.
 */
export function destinoDe(m: DefModulo, p: Permisos): string | null {
  const subs = submodulosDe(m, p);
  if (!subs.length) return null;
  const primero = m.landing && subs.includes(m.landing) ? m.landing : subs[0]!;
  const pg = paginasDe(primero, m.id, p)[0];
  return pg ? RUTA_DE[pg]!.ruta : null;
}

export interface Ubicacion {
  /** Nombre del módulo por el que se llega. */
  modulo: string;
  /** Título del submódulo, o `null` si el módulo entra directo. */
  submodulo: string | null;
  /** Título de la página dentro del submódulo, o `null` si el submódulo es la página. */
  hoja: string | null;
  ruta: string;
  /** Query que fija el módulo, para que el enlace abra el submenú correcto. */
  modId: string;
}

/**
 * `pathTo` del bloque de DHALIA (@90.579.052): dónde vive una página para ESTE
 * usuario. Devuelve todas las ubicaciones porque una misma página vive en
 * varios módulos —el v88 también las junta y ofrece las alternativas—, o
 * `'denegada'` si el perfil no la tiene.
 */
export function ubicacionesDe(pagina: string, p: Permisos): Ubicacion[] | 'denegada' {
  if (!p.puedeVer(pagina)) return 'denegada';
  const salida: Ubicacion[] = [];
  for (const m of modulosVisibles(p)) {
    for (const sub of submodulosDe(m, p)) {
      const pgs = paginasDe(sub, m.id, p);
      if (!pgs.includes(pagina)) continue;
      const c = CAT[sub]!;
      const esHoja = !!c.kids && pgs.length > 1;
      salida.push({
        modulo: m.nombre,
        submodulo: sub.startsWith('@') ? null : c.t,
        hoja: esHoja ? RUTA_DE[pagina]!.titulo : null,
        ruta: RUTA_DE[pagina]!.ruta,
        modId: m.id,
      });
    }
  }
  return salida;
}
